import {
  CheckSquareOutlined,
  ClearOutlined,
  MoreOutlined,
  SelectOutlined,
  ThunderboltOutlined,
} from "@ant-design/icons";
import type { ComponentType, ReactNode } from "react";
import type {
  AdminActionButtonVisual,
  AdminActionIntent,
  AdminResolvedActionButtonVisual,
} from "./admin-action-types";
import { getSemanticActionPresentation, type SemanticAction } from "./semantic-action";

type IconComponent = ComponentType<Record<string, never>>;

const ADMIN_ACTION_ICONS = {
  CheckSquareOutlined,
  ClearOutlined,
  MoreOutlined,
  SelectOutlined,
  ThunderboltOutlined,
} satisfies Record<string, IconComponent>;

type AdminIconName = keyof typeof ADMIN_ACTION_ICONS;

export function resolveAdminActionButtonVisual(
  action: AdminActionButtonVisual,
): AdminResolvedActionButtonVisual {
  const isPrimaryIntent =
    action.intent === "create" ||
    action.intent === "publish" ||
    action.intent === "run" ||
    action.intent === "apply" ||
    action.intent === "select";
  return {
    color: action.color ?? (action.danger ? "danger" : isPrimaryIntent ? "primary" : "default"),
    variant: action.variant ?? (isPrimaryIntent ? "solid" : "outlined"),
    danger: Boolean(action.danger),
  };
}

export function shouldUseColoredActionIcon(
  buttonVisual: Pick<AdminResolvedActionButtonVisual, "variant">,
) {
  return buttonVisual.variant !== "solid" && buttonVisual.variant !== "filled";
}

export function AdminActionIcon({ name }: { name: AdminIconName }) {
  const Icon = ADMIN_ACTION_ICONS[name];
  return <Icon />;
}

export function resolveAdminActionPresentation(action: {
  key: string;
  icon?: ReactNode;
  intent?: AdminActionIntent;
  semantic?: SemanticAction;
  danger?: boolean;
}) {
  if (action.icon) {
    return { icon: action.icon, color: undefined, unresolvedLabel: undefined };
  }
  return getSemanticActionPresentation({
    key: action.key,
    semantic: action.semantic,
    intent: action.intent,
    danger: action.danger,
  });
}

export function renderAdminActionIcon(
  action: {
    key: string;
    icon?: ReactNode;
    intent?: AdminActionIntent;
    semantic?: SemanticAction;
    danger?: boolean;
  },
  options?: { colored?: boolean },
) {
  const colored = options?.colored ?? true;
  const presentation = resolveAdminActionPresentation(action);
  const icon = presentation.icon;
  if (!colored) return icon;
  return <span style={presentation.color ? { color: presentation.color } : undefined}>{icon}</span>;
}
